import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from 'axios';
import { PREBUILT_EXAM_CATALOG } from '../../utils/prebuiltExamCatalog';
import OvalSpinner from "../UI/Spinners/OvalSpinner";
import { useNavigate } from "react-router-dom";
import AuthHeader from "../UI/AuthHeader/AuthHeader";
import AuthSideMenu from "../UI/AuthSideMenu/AuthSideMenu";
import { Grid, Card, CardContent, Typography, Slider } from "@mui/material";
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { FaArrowRight } from "react-icons/fa";
import { FaWandMagicSparkles } from "react-icons/fa6";
import Button from "../UI/Buttons/Button";
import AuthInputs from "../UI/Input/AuthInputs";
import InputHandler from "../Resume/Tools/InputHandler";
import uploadBtnStyles from '../UI/UploadPasteButton.module.css';
import { checkAuthenticatedUser, startExamSession } from "../../utils/client-functions";
import { PageDataContainerStyled, SliderBox } from "../../styled/Resume";
import { useDispatch } from 'react-redux';
import { useErrorSetter } from '../../hooks/useUiMessages';
import useExamStartGate from "../../hooks/useExamStartGate";
import { Modal } from "../UI/Modal/Modal";
import ExamReadyModal from "./ExamReadyModal";
import { TypeAnimation } from 'react-type-animation';
import BubbleFloat from "../UI/BubbleFloat/BubbleFloat";
import Rules from "./Rules";
import UpgradePopup from "../UI/SubscriptionPlans/UpgradePopup";
import { useTheme } from "../UI/Theme/ThemeContext";
import styled from "styled-components";
import bubblebg from "../../images/bubblebannerbg.png";

const TypeWrite = styled.div`
    width: auto;
    margin-top: 0.75rem;
    font-size: .85rem;
    text-align: center;
    font-weight: 600;
    color: #fff;
    border-radius: 20px;
    padding: 15px 10px;
    min-height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    background:
            linear-gradient(to right, rgba(111, 203, 209, 0.4), rgba(57, 104, 107, 0.8)),
            url(${bubblebg}) center center;
    background-size: cover;
    
    @media (min-width: 900px) {
        font-size: 1.1rem;
    }
`

const ExamCard = styled(Card)`
    height: 100%;
    display: flex;
    flex-direction: column;
    border: 2px solid #5fbec5 !important;
    border-radius: 12px !important;
    transition: all 0.3s ease !important;
    cursor: pointer;
    position: relative;
    background: ${props => props.theme === 'dark' ? 'rgba(12, 30, 40, 0.95)' : '#fff'} !important;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(95, 190, 197, 0.3) !important;
        border-color: #3E8F93 !important;
    }
`

const IconWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1.5rem 0 1rem 0;
    color: #3E8F93;
`

const TakeMockButton = styled.button`
    position: absolute;
    bottom: 1rem;
    right: 1rem;
    background: linear-gradient(135deg, #3E8F93 0%, #5fbec5 100%);
    color: white;
    border: none;
    border-radius: 8px;
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.3s ease;
    
    &:hover {
        background: linear-gradient(135deg, #2d6d75 0%, #3E8F93 100%);
        transform: translateX(2px);
    }
    
    &:active {
        transform: scale(0.98);
    }
`


const MockTests = (props) => {
    const navigate = useNavigate();
    const { themeName } = useTheme();
    const [viewMode, setViewMode] = useState('selection');

    const [uploadText, setUploadText] = useState("");
    const [uploadFiles, setUploadFiles] = useState([]);
    const [uploadPreviews, setUploadPreviews] = useState([]);
    const [examDuration, setExamDuration] = useState(60);
    const [questionType, setQuestionType] = useState('MCQ');
    const [questionCount, setQuestionCount] = useState(20);
    const fileInputRef = useRef(null);

    const durationMarks = [
        { value: 30, label: '30m' },
        { value: 60, label: '1h' },
        { value: 90, label: '1.5h' },
        { value: 120, label: '2h' },
        { value: 150, label: '2.5h' },
        { value: 180, label: '3h' },
        { value: 210, label: '3.5h' },
        { value: 240, label: '4h' },
    ];

    const handleUploadClick = () => {
        setViewMode('upload');
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length + uploadFiles.length > 5) {
            errorSetter("Max 5 files allowed");
            return;
        }

        const newPreviews = files.map(file => {
            if (file.type === 'application/pdf') {
                return 'pdf-placeholder'; // Special placeholder for PDF
            }
            return URL.createObjectURL(file);
        });
        setUploadPreviews(prev => [...prev, ...newPreviews]);
        setUploadFiles(prev => [...prev, ...files]);
    };

    const removeFile = (index) => {
        setUploadPreviews(prev => prev.filter((_, i) => i !== index));
        setUploadFiles(prev => prev.filter((_, i) => i !== index));
    };

    const triggerFileSelect = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const [menuOpen, setMenuOpen] = useState(false);
    const [searchString, setSearchString] = useState("");
    const [textColor, setTextColor] = useState('#3E8F93');
    const [searchArray, setSearchArray] = useState([]);
    const [searching, setSearching] = useState(false);
    const [checkingRemote, setCheckingRemote] = useState(false);
    const [foundExam, setFoundExam] = useState(null);
    const [foundExams, setFoundExams] = useState([]);
    const [selectedFoundExam, setSelectedFoundExam] = useState(null);
    const [showFoundExamModal, setShowFoundExamModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [progressStatus, setProgressStatus] = useState('Gathering Exam Details...');
    const [progressPercentage, setProgressPercentage] = useState(0);
    const dispatch = useDispatch();
    const errorSetter = useErrorSetter();
    const { examReady, pendingExamDetails, handleOngoing, handleDoneEvent, confirmStartExam, closeGate } = useExamStartGate(navigate, dispatch);
    const debounceTimerRef = useRef(null);
    const screenWidth = window.innerWidth
    const searchBarWidth = screenWidth >= 900 ? '50%' : '95%';
    
  

    useEffect(() => {
        const verifyAuth = async () => {
            try {
                await checkAuthenticatedUser();
            } catch (error) {
                errorSetter("Please login to access mock exams");
                sessionStorage.setItem("prevPath", "/user/dashboard/mock");
                navigate("/popin");
            }
        };
        verifyAuth();
    }, [navigate, errorSetter]);

    const handleGenerateCustomExam = async () => {
        let progressInterval = null;
        try {
            await checkAuthenticatedUser();
        } catch (error) {
            errorSetter("Please login to generate an exam");
            sessionStorage.setItem("prevPath", "/user/dashboard/mock");
            return navigate("/popin");
        }

        try {
            if (uploadFiles.length < 1 && uploadText.trim().length < 200) {
                return errorSetter(uploadText.trim().length === 0 
                    ? "Please provide me some study material (text or files)" 
                    : "Input text must be at least 200 characters");
            }
            setLoading(true);
            setProgressStatus("Preparing your study materials...");
            const initialProgress = Math.floor(Math.random() * 6); // 0-5%
            setProgressPercentage(initialProgress);

            let currentFEProgress = initialProgress;
            progressInterval = setInterval(() => {
                if (currentFEProgress < 56) {
                    // Random increment +1 to +3
                    currentFEProgress += Math.floor(Math.random() * 3) + 1;
                    if (currentFEProgress > 56) currentFEProgress = 56;
                    setProgressPercentage(currentFEProgress);
                }
            }, 5000);

            const token = localStorage.getItem('token');
            const s3Files = [];

            // 1. Upload files to S3 via presigned URLs if any
            if (uploadFiles.length > 0) {
                const filesMetadata = uploadFiles.map(f => ({ originalname: f.name, size: f.size }));
                setProgressStatus("Checking quotas and cache...");
                
                for (let i = 0; i < uploadFiles.length; i++) {
                    const file = uploadFiles[i];
                    
                    // a. Get presigned URL
                    const { data } = await axios.post('/mock/generate-presigned-url', {
                        fileName: file.name,
                        fileType: file.type,
                        fileSize: file.size,
                        text: uploadText,
                        questionType,
                        questionCount,
                        filesMetadata: filesMetadata 
                    }, { headers: { 'x-access-token': token } });

                    if (data.status === 'cached') {
                        clearInterval(progressInterval);
                        setLoading(false);
                        handleOngoing(data.examDetails);
                        return;
                    }

                    const { uploadUrl, key } = data;

                    // b. Upload to S3
                    setProgressStatus("Uploading study materials, larger materials take time...");
                    await axios.put(uploadUrl, file, {
                        headers: { 'Content-Type': file.type }
                    });

                    s3Files.push({
                        key,
                        originalname: file.name,
                        mimetype: file.type,
                        size: file.size
                    });
                }
            }

            // Move closer to the runner's handoff
            setProgressPercentage(56);
            clearInterval(progressInterval);

            setProgressStatus("Scanning your study materials...");

            // 2. Submit job with S3 keys
            const payload = {
                text: uploadText,
                duration: examDuration * 60,
                questionType,
                questionCount,
                s3Files
            };

            await startExamSession({
                endpoint: '/mock/generate-custom',
                payload: payload,
                tokenOverride: token,
                onOngoing: (examDetails) => { setLoading(false); handleOngoing(examDetails); },
                onDone: (e) => { 
                    handleDoneEvent(e); 
                    setLoading(false); 
                },
                onError: (msg) => { setLoading(false); errorSetter(msg); },
                onJobCreated: (jobId) => { 
                    // Job created, SSE will take over
                },
                setLoading, setProgressStatus, setProgressPercentage
            });

        } catch (err) {
            if (progressInterval) clearInterval(progressInterval);
            console.error(err);
            setLoading(false);
            errorSetter(err.response?.data?.message || err.response?.data?.error || "An Error Occurred. Please try again. If issue persists, please chat with us.");
            if (err.response?.data?.openPricing) {
                window.dispatchEvent(new CustomEvent('openExamPricing'));
            }
        }
    };

    const toggleSideMenu = () => {
        setMenuOpen(!menuOpen)
    }

    const examIconImg = <ImportContactsIcon sx={{fontSize: '4rem'}} />;

    const uploadDisplay = (
        <PageDataContainerStyled theme={themeName}>
            <div style={{ 
                padding: screenWidth < 768 ? '0' : '0 1rem', 
                maxWidth: '850px', 
                margin: '0 auto', 
                width: '100%' 
            }}>
                <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '1rem' }}>
                    <div 
                        onClick={() => setViewMode('prebuilt')}
                        style={{ 
                            cursor: 'pointer', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            padding: screenWidth < 768 ? '5px 12px' : '6px 14px',
                            borderRadius: '20px',
                            border: '1px solid #5fbec5',
                            color: '#5fbec5',
                            fontSize: screenWidth < 768 ? '0.75rem' : '0.85rem'
                        }}
                    >
                        <LibraryBooksIcon sx={{ fontSize: screenWidth < 768 ? '1rem' : '1.2rem', color: 'crimson' }} /> Take Prebuilt Mock Instead
                    </div>
                </div>

                <Typography variant="h4" component="h1" sx={{ 
                    textAlign: 'center', 
                    fontWeight: 700, 
                    mb: screenWidth < 768 ? 2 : 4, 
                    color: themeName === 'dark' ? '#fff' : '#3E8F93',
                    fontSize: screenWidth < 768 ? '1.5rem' : '2.125rem'
                }}>
                    Provide Study Materials
                </Typography>

                <div className="contentSection">
                    <div className="dragDropSection" style={{ gap: screenWidth < 768 ? '0.5rem' : '1rem' }}>
                        <div className="textareaWrapper">
                            <InputHandler
                                id="studyMaterialText"
                                type="textBox"
                                name="studyMaterialText"
                                value={uploadText}
                                placeholder="(PDF/TEXT/IMAGES) Paste your study materials, curriculum, or specific notes here..."
                                multiline={true}
                                onChange={(e) => setUploadText(e.target.value)}
                            />
                            <div className="uploadBtnBar" style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: screenWidth < 768 ? '10px' : '20px', 
                                flexWrap: 'wrap',
                                justifyContent: screenWidth < 768 ? 'space-between' : 'flex-start',
                                padding: screenWidth < 768 ? '0.5rem' : '0.75rem'
                            }}>
                                <button
                                    onClick={triggerFileSelect}
                                    title="Upload"
                                    className={uploadBtnStyles.uploadPasteBtn}
                                    type="button"
                                    style={{ 
                                        margin: 0, 
                                        marginTop: screenWidth < 768 ? '10px' : '20px',
                                        flex: screenWidth < 768 ? '1 1 100%' : '0 0 auto',
                                        fontSize: screenWidth < 768 ? '0.75rem' : 'inherit',
                                        order: 0
                                    }}
                                >
                                    <span className={uploadBtnStyles.uploadPasteBtnText}>Upload Doc/Images</span>
                                </button>

                                {/* Duration Slider */}
                                <SliderBox style={{ 
                                    flex: screenWidth < 768 ? '1 1 100%' : '1', 
                                    minWidth: screenWidth < 768 ? '100%' : '200px', 
                                    paddingTop: screenWidth < 768 ? '0.5rem' : '1.2rem', 
                                    margin: 0,
                                    order: screenWidth < 768 ? 3 : 1
                                }}>
                                    <Typography variant="caption" sx={{ 
                                        color: themeName === 'dark' ? '#5fbec5' : '#3E8F93', 
                                        fontWeight: 700, 
                                        display: 'block', 
                                        mb: 0.2, 
                                        fontSize: '0.6rem', 
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em'
                                    }}>
                                        Exam Duration
                                    </Typography>
                                    <Slider
                                        value={examDuration}
                                        onChange={(e, val) => setExamDuration(val)}
                                        step={30}
                                        marks={durationMarks}
                                        min={30}
                                        max={240}
                                        aria-label="exam-duration"
                                        sx={{
                                            color: '#5fbec5',
                                            height: 3,
                                            padding: '10px 0',
                                            '& .MuiSlider-rail': { opacity: 0.3, backgroundColor: 'rgba(95,190,197,0.30)' },
                                            '& .MuiSlider-track': { border: 'none', backgroundColor: '#5fbec5' },
                                            '& .MuiSlider-thumb': {
                                                height: 12,
                                                width: 12,
                                                backgroundColor: '#fff',
                                                border: '2px solid #5fbec5',
                                                boxShadow: 'none',
                                                '&:hover, &.Mui-focusVisible, &.Mui-active': { boxShadow: '0 0 0 6px rgba(95,190,197,0.16)' }
                                            },
                                            '& .MuiSlider-markLabel': { 
                                                fontSize: '0.55rem',
                                                mt: 0.2,
                                                color: themeName === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'
                                            }
                                        }}
                                    />
                                </SliderBox>

                                {/* Question Type Dropdown */}
                                <div style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: '2px',
                                    flex: screenWidth < 768 ? '1 1 calc(50% - 5px)' : '0 0 auto',
                                    order: screenWidth < 768 ? 1 : 2
                                }}>
                                    <Typography variant="caption" sx={{ 
                                        color: themeName === 'dark' ? '#5fbec5' : '#3E8F93', 
                                        fontWeight: 700, 
                                        fontSize: '0.6rem', 
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em'
                                    }}>
                                        Question Type
                                    </Typography>
                                    <select
                                        value={questionType}
                                        onChange={(e) => setQuestionType(e.target.value)}
                                        style={{
                                            height: screenWidth < 768 ? 32 : 36,
                                            padding: '0 8px',
                                            borderRadius: 10,
                                            border: '1px solid #5fbec5',
                                            background: themeName === 'dark' ? 'rgba(12, 30, 40, 0.8)' : '#fff',
                                            color: themeName === 'dark' ? '#fff' : '#0c1e28',
                                            fontSize: screenWidth < 768 ? '0.75rem' : '0.8rem',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            outline: 'none',
                                            width: '100%',
                                            minWidth: screenWidth < 768 ? 'auto' : '130px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        <option value="MCQ">Multi Choice</option>
                                        <option value="Theory">Theory/Others</option>
                                    </select>
                                </div>

                                {/* Question Count Input */}
                                <div style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: '2px',
                                    flex: screenWidth < 768 ? '1 1 calc(50% - 5px)' : '0 0 auto',
                                    order: screenWidth < 768 ? 2 : 3
                                }}>
                                    <Typography variant="caption" sx={{ 
                                        color: themeName === 'dark' ? '#5fbec5' : '#3E8F93', 
                                        fontWeight: 700, 
                                        fontSize: '0.6rem', 
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em'
                                    }}>
                                        Question Count
                                    </Typography>
                                    <input
                                        type="number"
                                        min="1"
                                        max="100"
                                        value={questionCount}
                                        onChange={(e) => setQuestionCount(e.target.value)}
                                        style={{
                                            height: screenWidth < 768 ? 32 : 36,
                                            padding: '0 8px',
                                            borderRadius: 10,
                                            border: '1px solid #5fbec5',
                                            background: themeName === 'dark' ? 'rgba(12, 30, 40, 0.8)' : '#fff',
                                            color: themeName === 'dark' ? '#fff' : '#0c1e28',
                                            fontSize: screenWidth < 768 ? '0.75rem' : '0.8rem',
                                            fontWeight: 500,
                                            outline: 'none',
                                            width: '100%',
                                            minWidth: screenWidth < 768 ? 'auto' : '80px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                        }}
                                    />
                                </div>

                                <input 
                                    ref={fileInputRef} 
                                    type="file" 
                                    multiple 
                                    accept="image/*,.pdf" 
                                    onChange={handleFileSelect} 
                                    hidden 
                                />
                            </div>
                        </div>

                        {/* Previews */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '1rem' }}>
                            {uploadPreviews.map((url, idx) => {
                                const isPDF = uploadFiles[idx]?.type === 'application/pdf';
                                return (
                                    <div key={idx} style={{ position: 'relative' }}>
                                        {isPDF ? (
                                            <div style={{ 
                                                width: 80, height: 80, borderRadius: 8, 
                                                background: themeName === 'dark' ? '#1a2e35' : '#e8f4f5',
                                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                                border: '1px solid #5fbec5', color: '#5fbec5', fontWeight: 600,
                                                fontSize: '0.75rem', textAlign: 'center', padding: '4px'
                                            }}>
                                                PDF File
                                                <div style={{ fontSize: '0.45rem', fontWeight: 400, opacity: 0.8, marginTop: '2px', wordBreak: 'break-all' }}>
                                                    {uploadFiles[idx]?.name ? (uploadFiles[idx].name.length > 20 ? uploadFiles[idx].name.substring(0, 17) + '...' : uploadFiles[idx].name) : ''}
                                                </div>
                                            </div>
                                        ) : (
                                            <img src={url} alt={`preview-${idx}`} style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', border: '1px solid #5fbec5' }} />
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => removeFile(idx)}
                                            style={{ 
                                                position: 'absolute', top: -6, right: -6, 
                                                border: 'none', background: 'crimson', color: '#fff', 
                                                borderRadius: '50%', width: 22, height: 22, cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: '14px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="optimizeBtnRow">
                    <Button
                        primary={true}
                        width="100%"
                        title="Generate Mock Exam"
                        clickHandle={handleGenerateCustomExam}
                        disabled={!uploadText.trim() && uploadFiles.length === 0}
                    >
                        <FaWandMagicSparkles />
                    </Button>
                </div>
            </div>
        </PageDataContainerStyled>
    );

    const cardStyles = {
        cardGrid: {
            padding: '0.5rem',
            flexBasis: { xs: '100%', md: '50%', lg: '33.333%' },
            maxWidth: { xs: '100%', md: '50%', lg: '33.333%' }
        },
        card: {
            minHeight: '280px',
            position: 'relative',
            paddingBottom: '4rem'
        }
    }

    // Stable exams array — sourced from shared catalog (client/src/utils/prebuiltExamCatalog.js)
    // To add a new exam here AND in TeacherDashboard, edit that file only.
    const mockTests = useMemo(() =>
        PREBUILT_EXAM_CATALOG.map(exam => ({
            title: exam.examName,
            description: exam.description,
            onClick: () => navigate(exam.examUrl),
        }))
    , [navigate]);

    // eslint-disable-next-line no-unused-vars
    // const _legacyExamEntries = [{
    //         title: "UTME",
    //         description: "Unified Tertiary Matriculation Examination Mock",
    //         onClick: () => {
    //             navigate("/user/dashboard/mock/utme")
    //         }
    //     },
    //     {
    //         title: "ICAN",
    //         description: "Institute of Chartered Accountants of Nigeria Mock Exams",
    //         onClick: () => {
    //             navigate("/user/dashboard/mock/ican")
    //         }
    //     },
    //     {
    //         title: "NCS",
    //         description: "Nigeria Customs Service Recruitment CBT Mock",
    //         onClick: () => {
    //             navigate("/user/dashboard/mock/ncs")
    //         }
    //     },
    //     {
    //         title: "SUBEB",
    //         description: "States Universal Basic Education Board Recruitment CBT Mock",
    //         onClick: () => {
    //             navigate("/user/dashboard/mock/subeb")
    //         }
    //     },
    //     {
    //         title: "FCSC",
    //         description: "Federal Civil Service Commission Recruitment CBT Mock",
    //         onClick: () => {
    //             navigate("/user/dashboard/mock/fcsc")
    //         }
    //     },
    //     {
    //         title: "TRCN PQE",
    //         description: "Teachers Registration Council of Nigeria PQE Mock",
    //         onClick: () => {
    //             navigate("/user/dashboard/mock/trcn")
    //         }
    //     },
    //     {
    //         title: "NSCDC",
    //         description: "Nigeria Security and Civil Defence Corps Recruitment CBT Mock",
    //         onClick: () => {
    //             navigate("/user/dashboard/mock/nscdc")
    //         }
    //     },
    //     {
    //         title: "Federal Fire Service (Nigeria)",
    //         description: "Federal Fire Service (Nigeria) Recruitment CBT Mock",
    //         onClick: () => {
    //             navigate("/user/dashboard/mock/ffs")
    //         }
    //     },
    //     {
    //         title: "NIS",
    //         description: "Nigeria Immigration Service Recruitment CBT Mock",
    //         onClick: () => {
    //             navigate("/user/dashboard/mock/nis")
    //         }
    //     },
    //     {
    //         title: "FCSC",
    //         description: "Federal Civil Service Commission Recruitment CBT Mock",
    //         onClick: () => {
    //             navigate("/user/dashboard/mock/fcsc")
    //         }
    //     },
    //     // {
    //     //     title: "IELTS",
    //     //     description: "International English Language Testing System Mock Exams",
    //     //     onClick: () => {
    //     //         navigate("/user/dashboard/mock/ielts")
    //     //     }
    //     // },
    //     {
    //         title: "WASSCE",
    //         description: "The West African Senior School Certificate Examination Mock",
    //         onClick: () => {
    //             navigate("/user/dashboard/mock/wassce")
    //         }
    //     },
    //     // {
    //     //     title: "TOEFL",
    //     //     description: "Test Of English as a Foreign Language Mock",
    //     //     onClick: () => {
    //     //         navigate("/user/dashboard/mock/toefl")
    //     //     }
    //     // },
    //     // {
    //     //     title: "CEE",
    //     //     description: "Cambridge English Mock Exam",
    //     //     onClick: () => {
    //     //         navigate("/user/dashboard/mock/cee")
    //     //     }
    //     // },
    //     // {
    //     //     title: "NECO",
    //     //     description: "National Examination Council Mock Exams",
    //     //     onClick: () => {
    //     //         navigate("/user/dashboard/mock/neco")
    //     //     }
    //     // },
    //     // {
    //     //     title: "GRE",
    //     //     description: "Graduate Record Examination Mock Exams",
    //     //     onClick: () => {
    //     //         navigate("/user/dashboard/mock/gre")
    //     //     }
    //     // },
    //     // {
    //     //     title: "CEH",
    //     //     description: "Certified Ethical Hacker Mock Exams",
    //     //     onClick: () => {
    //     //         navigate("/user/dashboard/mock/ceh")
    //     //     }
    //     // },
    //     // {
    //     //     title: "PMP",
    //     //     description: "Project Management Professional Mock Exams",
    //     //     onClick: () => {
    //     //         navigate("/user/dashboard/mock/pmp")
    //     //     }
    //     // },
    //     // {
    //     //     title: "CISSP",
    //     //     description: "Certified Information Systems Security Professional Mock Exams",
    //     //     onClick: () => {
    //     //         navigate("/user/dashboard/mock/cissp")
    //     //     }
    //     // },
    //     // {
    //     //     title: "CIMA",
    //     //     description: "Chartered Institute of Management Accountants Mock Exams",
    //     //     onClick: () => {
    //     //         navigate("/user/dashboard/mock/cima")
    //     //     }
    //     // },
    //     // {
    //     //     title: "FRM",
    //     //     description: "Financial Risk Manager Mock Exams",
    //     //     onClick: () => {
    //     //         navigate("/user/dashboard/mock/frm")
    //     //     }
    //     // },
    //     // {
    //     //     title: "CFA",
    //     //     description: "Chartered Financial Analyst Mock Exams",
    //     //     onClick: () => {
    //     //         navigate("/user/dashboard/mock/cfa")
    //     //     }
    //     // },
    //     // {
    //     //     title: "ACCA",
    //     //     description: "Association of Chartered Certified Accountants Mock Exams",
    //     //     onClick: () => {
    //     //         navigate("/user/dashboard/mock/acca")
    //     //     }
    //     // }
    // ]; // eslint-disable-line -- legacy entries kept for reference, not used

    
    useEffect(() => {
        // Debounced search with remote exam existence check when no local match
        const s = searchString.trim();
        const filteredData = mockTests.filter(item => 
            item.title.toLowerCase().includes(s.toLowerCase()) ||
            item.description.toLowerCase().includes(s.toLowerCase())
        );
        setSearchArray(filteredData);
        setSearching(s !== "");
        setFoundExam(null);
        setFoundExams([]);
        setSelectedFoundExam(null);
        setCheckingRemote(false);

        if (!s) {
            return;
        }

        if (filteredData.length === 0) {
            // debounce 2 seconds of inactivity then call backend to check exam
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = setTimeout(async () => {
                setCheckingRemote(true);
                try {
                    const token = localStorage?.getItem('token');
                    const resp = await axios.post('/mock/check-exam-exists', { query: s }, { headers: { 'x-access-token': token } });
                    const exam = resp?.data?.examDetails || null;

                    const normalizeFront = (item) => {
                        if (!item) return null;
                        // already normalized shape
                        if (item.examMeta) return item;
                        // model returned raw exam object; normalize to frontend shape
                        const time = item.timeAlloted || item.timeAllotted || item.duration || 2700;
                        return {
                            examMeta: {
                                examName: item.examName || item.name || item.title || item.acronym || 'Generated Exam',
                                acronym: item.acronym || (item.examName && item.examName.split(' ').map(w=>w[0]).join('').slice(0,6)) || '',
                                subject: item.subject || item.acronym || item.examName || '',
                                description: item.description || '',
                                duration: time,
                                recommendedQuestionCount: item.recommendedQuestionCount || item.recommendedQuestions || 60
                            },
                            subject: {
                                subject: item.subject || item.acronym || item.examName || '',
                                description: item.description || '',
                                timeAlloted: time
                            },
                            creationHint: item.creationHint || ''
                        };
                    };

                    if (Array.isArray(exam)) {
                        const normalizedArr = exam.map(normalizeFront).filter(x=>x!==null);
                        console.log('[MockTests] normalizedArr:', normalizedArr);
                        setFoundExams(normalizedArr);
                        setFoundExam(null);
                    } else if (exam) {
                        const normalized = normalizeFront(exam);
                        console.log('[MockTests] normalized single exam:', normalized);
                        setFoundExam(normalized);
                        setFoundExams([]);
                    } else {
                        setFoundExam(null);
                        setFoundExams([]);
                    }
                } catch (err) {
                    // treat as not found
                    setFoundExam(null);
                    setFoundExams([]);
                } finally {
                    setCheckingRemote(false);
                }
            }, 2000);
        }

        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, [searchString, mockTests]);

    const handleSearchInput = (e) => {
        setSearchString(e.target.value);
    };

    const searchDisplay = (
        <Grid container spacing={2} sx={{ padding: { xs: 0, md: '2rem' }, justifyContent: 'center' }}>
            {searchArray.length > 0 && searchArray.map(({ title, description, onClick }, index) => (
                <Grid item xs={12} md={6} lg={4} key={index} sx={cardStyles.cardGrid}>
                    <ExamCard onClick={onClick} sx={cardStyles.card} theme={themeName}>
                        <IconWrapper>
                            {examIconImg}
                        </IconWrapper>
                        <CardContent sx={{ textAlign: 'center', flex: 1 }}>
                            <Typography variant="h5" component="div" sx={{ fontWeight: 700, mb: 1, color: themeName === 'dark' ? '#fff' : '#3E8F93' }}>
                                {title}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.9rem', opacity: 0.85, color: themeName === 'dark' ? '#fff' : '#000' }}>
                                {description}
                            </Typography>
                        </CardContent>
                        <TakeMockButton onClick={(e) => { e.stopPropagation(); onClick(); }}>
                            Take Mock Exam <FaArrowRight />
                        </TakeMockButton>
                    </ExamCard>
                </Grid>
            ))}

            {searchArray.length === 0 && (
                // Ensure every branch renders Grid items (children of the top-level Grid container)
                <>
                    {checkingRemote ? (
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <OvalSpinner color={themeName === 'dark' ? '#fff' : '#3E8F93'} />
                            <div style={{ fontWeight: 700, color: themeName === 'dark' ? '#fff' : 'inherit' }}>Checking if exam is a real exam</div>
                        </Grid>
                    ) : foundExams && foundExams.length > 0 ? (
                        foundExams.map((fe, idx) => (
                            <Grid item xs={12} md={6} lg={4} key={idx} sx={cardStyles.cardGrid}>
                                <ExamCard onClick={() => { setSelectedFoundExam(fe); setShowFoundExamModal(true); }} sx={cardStyles.card} theme={themeName}>
                                    <IconWrapper>{examIconImg}</IconWrapper>
                                    <CardContent sx={{ textAlign: 'center', flex: 1 }}>
                                        <Typography variant="h5" component="div" sx={{ fontWeight: 700, mb: 1, color: themeName === 'dark' ? '#fff' : '#3E8F93' }}>
                                            {fe.examMeta?.acronym || fe.examMeta?.examName || 'Generated Exam'}
                                        </Typography>
                                        <Typography variant="subtitle2" sx={{ fontSize: '.9rem', color: themeName === 'dark' ? '#fff' : '#000' }}>
                                            {fe.examMeta?.examName || ''}
                                        </Typography>
                                    </CardContent>
                                    <TakeMockButton onClick={(e) => { e.stopPropagation(); setSelectedFoundExam(fe); setShowFoundExamModal(true);}}>
                                        View Exam <FaArrowRight />
                                    </TakeMockButton>
                                </ExamCard>
                            </Grid>
                        ))
                    ) : foundExam ? (
                        <Grid item xs={12} md={6} lg={4} sx={cardStyles.cardGrid}>
                            <ExamCard onClick={() => { setSelectedFoundExam(foundExam); setShowFoundExamModal(true); }} sx={cardStyles.card} theme={themeName}>
                                <IconWrapper>{examIconImg}</IconWrapper>
                                <CardContent sx={{ textAlign: 'center', flex: 1 }}>
                                    <Typography variant="h5" component="div" sx={{ fontWeight: 700, mb: 1, color: themeName === 'dark' ? '#fff' : '#3E8F93' }}>
                                        {foundExam.examMeta?.acronym || foundExam.examMeta?.examName || 'Generated Exam'}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ fontSize: '.9rem', color: themeName === 'dark' ? '#fff' : '#000' }}>
                                        {foundExam.examMeta?.examName || ''}
                                    </Typography>
                                </CardContent>
                                <TakeMockButton onClick={(e) => { e.stopPropagation(); setSelectedFoundExam(foundExam); setShowFoundExamModal(true); }}>
                                    View Exam <FaArrowRight />
                                </TakeMockButton>
                            </ExamCard>
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            <span style={{ color: themeName === 'dark' ? '#fff' : 'inherit', display: 'block', textAlign: 'center' }}>Exams not found. Double check spelling.</span>
                        </Grid>
                    )}
                </>
            )}
        </Grid>
    );

    const selectionDisplay = (
        <Grid container spacing={2} sx={{ padding: { xs: 0, md: '2rem' }, justifyContent: 'center' }}>
            <Grid item xs={12} md={6} lg={4} sx={cardStyles.cardGrid}>
                <ExamCard onClick={handleUploadClick} sx={cardStyles.card} theme={themeName}>
                    <IconWrapper>
                        <CloudUploadIcon sx={{fontSize: '4rem'}} />
                    </IconWrapper>
                    <CardContent sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="h5" component="div" sx={{ fontWeight: 700, mb: 1, color: themeName === 'dark' ? '#fff' : '#3E8F93' }}>
                            Upload Study Materials
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', opacity: 0.85, color: themeName === 'dark' ? '#fff' : '#000' }}>
                            Upload/paste your curriculum, textbooks, or notes, and let Bubble Ai generate custom mock exams specifically from your study materials.
                        </Typography>
                    </CardContent>
                    <TakeMockButton onClick={(e) => { e.stopPropagation(); handleUploadClick(); }}>
                        Start Here <FaArrowRight />
                    </TakeMockButton>
                </ExamCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4} sx={cardStyles.cardGrid}>
                <ExamCard onClick={() => setViewMode('prebuilt')} sx={cardStyles.card} theme={themeName}>
                    <IconWrapper>
                        <LibraryBooksIcon sx={{fontSize: '4rem'}} />
                    </IconWrapper>
                    <CardContent sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="h5" component="div" sx={{ fontWeight: 700, mb: 1, color: themeName === 'dark' ? '#fff' : '#3E8F93' }}>
                            Take PreBuilt Exam
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', opacity: 0.85, color: themeName === 'dark' ? '#fff' : '#000' }}>
                            Test your knowledge with our extensive collection of curated mock exams for known professional and academic certifications.
                        </Typography>
                    </CardContent>
                    <TakeMockButton onClick={(e) => { e.stopPropagation(); setViewMode('prebuilt'); }}>
                        Explore Exams <FaArrowRight />
                    </TakeMockButton>
                </ExamCard>
            </Grid>
        </Grid>
    );

    const normalDisplay = (
        <div style={{ padding: '0 2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '1rem' }}>
                <div 
                    onClick={handleUploadClick}
                    style={{ 
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        border: '1px solid #5fbec5',
                        color: '#5fbec5',
                        fontSize: '0.85rem'
                    }}
                >
                    <CloudUploadIcon sx={{ fontSize: '1.2rem', color: 'crimson' }} /> Upload materials Instead
                </div>
            </div>

            <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
                {mockTests.map((test, index) => {
                    return(
                        <Grid item xs={12} md={6} lg={4} key={index} sx={cardStyles.cardGrid}>
                            <ExamCard onClick={test.onClick} sx={cardStyles.card} theme={themeName}>
                                <IconWrapper>
                                    {examIconImg}
                                </IconWrapper>
                                <CardContent sx={{ textAlign: 'center', flex: 1 }}>
                                    <Typography variant="h5" component="div" sx={{ fontWeight: 700, mb: 1, color: themeName === 'dark' ? '#fff' : '#3E8F93' }}>
                                        {test.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.9rem', opacity: 0.85, color: themeName === 'dark' ? '#fff' : '#000' }}>
                                        {test.description}
                                    </Typography>
                                </CardContent>
                                <TakeMockButton onClick={(e) => { e.stopPropagation(); test.onClick(); }}>
                                    Take Mock Exam <FaArrowRight />
                                </TakeMockButton>
                            </ExamCard>
                        </Grid>
                    )
                })}
            </Grid>
        </div>
    );

  return (
    <div className="auth-container">
        {/* For SIDE MENU */}
        <AuthSideMenu opened={menuOpen} arrayDetails={[]} onSwitch={() => {}} />
       <BubbleFloat/>

        <div className="auth-container-inner">
            {/* for TOP MENU */}
            <AuthHeader
                authMenuOpen={menuOpen}
                onClick={toggleSideMenu}
                headerText="Choose Exam"
            />
            
            <div style={{ padding: '0 1rem', maxWidth: '1400px', margin: '0 auto' }}>
                {viewMode !== 'upload' && (
                    <>
                        <TypeWrite onClick={() => setMenuOpen(false)} style={{borderColor: textColor}}>
                            <TypeAnimation
                                sequence={[
                                    1000,
                                    '🏆 ACE Professional & Academic Exams with Ease',
                                    1000,
                                    '⏱️ Simulate Time-Based Mock Tests',
                                    1000,
                                    '📚 10 years+ of Randomized Past Questions',
                                    1000,
                                    '🎓 Get Tutored by Bubble Ai on Weak Areas',
                                    1000,
                                    '📋 Receive a Personalized Study Plan',
                                    1000,                            
                                    '📹 Get Study Videos',
                                    1000,
                                    '📝 Get Study Notes',
                                    1000,
                                    '🔄 Retake Tests to Improve Scores',
                                    1000,
                                    '📊 Track your Improvement',
                                    1000,
                                ]}
                                repeat={Infinity}
                            />
                        </TypeWrite>

                        <div style={{width: searchBarWidth, margin: "1rem auto"}}>
                            <AuthInputs
                                id={"searchString"}
                                name={searchString}
                                value={searchString}
                                onChange={handleSearchInput}
                                placeholder="Tell me the Exam"
                                inputType="search"
                                mb={1}
                                required={true}
                            />
                        </div>
                    </>
                )}

                {!searching ? (viewMode === 'selection' ? selectionDisplay : (viewMode === 'upload' ? uploadDisplay : normalDisplay)) : searchDisplay}

                {showFoundExamModal && (selectedFoundExam || foundExam) && (
                    <Rules
                        examName={(selectedFoundExam || foundExam).examMeta?.examName || (selectedFoundExam || foundExam).examMeta?.acronym}
                        subject={(selectedFoundExam || foundExam).subject}
                        closeModalsAndReset={() => { setShowFoundExamModal(false); setSelectedFoundExam(null); }}
                        handleStartExam={async () => {
                            const active = selectedFoundExam || foundExam;
                            try {
                                await checkAuthenticatedUser();
                            } catch (error) {
                                return navigate("/popin?mock");
                            }

                            setLoading(true);
                            setProgressStatus("Gathering Exam Details...");
                            setProgressPercentage(5);

                            const token = localStorage?.getItem('token');
                            const payload = {
                                subject: active.subject?.subject || active.examMeta?.examName || active.examMeta?.acronym,
                                timeAlloted: active.subject?.timeAlloted || active.subject?.timeAllotted || 2700,
                                description: active.subject?.description || '',
                                examName: active.examMeta?.examName || active.examMeta?.acronym || (active.subject && active.subject.subject),
                                totalQuestions: active.subject?.totalQuestions || 60,
                                passMarkPercent: active.subject?.passMarkPercent || 50,
                            };

                            try {
                                await startExamSession({
                                    payload,
                                    tokenOverride: token,
                                    onOngoing: (examDetails) => { setLoading(false); handleOngoing(examDetails); },
                                    onDone: (e) => { handleDoneEvent(e); },
                                    onError: (msg) => { setLoading(false); errorSetter(msg); },
                                    onJobCreated: (jobId) => { setShowFoundExamModal(false); },
                                    setLoading, setProgressStatus, setProgressPercentage
                                });
                            } catch (e) {
                                console.error('[MockTests] start-exam POST failed', e);
                                setLoading(false);
                                errorSetter(e.response?.data?.message || e.response?.data?.error || 'An Error Occurred. Please try again. If issue persists, please chat with us.');
                                if (e.response?.data?.openPricing) {
                                    window.dispatchEvent(new CustomEvent('openExamPricing'));
                                }
                            }
                        }}
                    />
                )}

                {loading && (
                    <Modal
                        header4={`PROCESSING... DO NOT RELOAD`}
                        header3={progressStatus}
                        progress={progressPercentage}
                    />
                )}

                {examReady && (
                    <ExamReadyModal
                        isOpen={examReady}
                        onClose={closeGate}
                        onStart={confirmStartExam}
                        examLabel={pendingExamDetails?.examMeta?.examName || pendingExamDetails?.examMeta?.acronym || (selectedFoundExam || foundExam)?.examMeta?.examName || (selectedFoundExam || foundExam)?.examMeta?.acronym}
                    />
                )}
            </div>
            <UpgradePopup />
        </div>
    </div>
  );
};

export default MockTests;
